/**
 * VM Connection Info Component
 * Displays subdomain, SSH instructions, and password management
 */

import React, { useState } from 'react';
import { VM } from '../../types/vm';
import { vmApi } from '../../services/vmApi';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface VMConnectionInfoProps {
  vm: VM;
}

export const VMConnectionInfo: React.FC<VMConnectionInfoProps> = ({ vm }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Get username from OS image name
  // Common mappings: ubuntu:* -> ubuntu, debian:* -> debian, etc.
  const getUsernameFromOSImage = (osImage: string): string => {
    const osLower = osImage.toLowerCase();
    if (osLower.startsWith('ubuntu') || osLower.startsWith('tensorflow') || osLower.startsWith('pytorch') || osLower.startsWith('n8n')) {
      return 'ubuntu';
    } else if (osLower.startsWith('debian')) {
      return 'debian';
    } else if (osLower.startsWith('centos') || osLower.startsWith('fedora') || osLower.startsWith('rhel')) {
      return 'root'; // CentOS/Fedora/RHEL typically use root
    }
    // Default to ubuntu for unknown OS
    return 'ubuntu';
  };

  const username = getUsernameFromOSImage(vm.os_image);

  // Generate SSH command with ProxyCommand
  // If SSH key is selected, include -i flag with key file path placeholder
  const getSSHCommand = (): string => {
    if (!vm.public_subdomain) return '';
    const subdomain = vm.public_subdomain;
    const proxyCommand = `openssl s_client -quiet -connect ${subdomain}:22 -servername ${subdomain}`;
    
    // If SSH key is selected, include -i flag for key file
    if (vm.ssh_key_id) {
      return `ssh -i ./ssh-key-file-name.pem -o ProxyCommand='${proxyCommand}' ${username}@${subdomain}`;
    } else {
      // No SSH key, password will be prompted
      return `ssh -o ProxyCommand='${proxyCommand}' ${username}@${subdomain}`;
    }
  };

  const sshCommand = getSSHCommand();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSetPassword = async () => {
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await vmApi.updateSSHPassword(vm.id, {
        password,
        confirm_password: confirmPassword,
      });
      toast.success(response.message);
      if (response.requires_reboot) {
        toast('VM will need to be restarted for password to take effect', {
          icon: 'ℹ️',
          duration: 5000,
        });
      }
      setShowPasswordForm(false);
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update SSH password');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Public Subdomain */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Public Subdomain
        </label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-800 px-3 py-2 rounded text-sm font-mono text-gray-300">
            {vm.public_subdomain || 'Not available'}
          </code>
          {vm.public_subdomain && (
            <button
              onClick={() => copyToClipboard(vm.public_subdomain!)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm font-medium transition-colors text-gray-300"
              title="Copy subdomain"
            >
              <ClipboardIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* SSH Instructions */}
      {vm.status === 'running' && vm.public_subdomain && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="font-semibold mb-2 text-white">SSH Connection Instructions</h4>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium text-gray-300 mb-2">Connect via SSH (TLS-wrapped):</p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <code className="flex-1 bg-gray-800 px-3 py-2 rounded text-xs font-mono break-all text-gray-300">
                    {sshCommand}
                  </code>
                  <button
                    onClick={() => copyToClipboard(sshCommand)}
                    className="px-2 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400 text-xs font-medium transition-colors flex-shrink-0"
                    title="Copy SSH command"
                  >
                    <ClipboardIcon className="w-4 h-4" />
                  </button>
                </div>
                {vm.ssh_key_id && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-xs">
                    <p className="text-yellow-300 font-medium mb-1">Note: SSH Key Authentication</p>
                    <p className="text-yellow-400/80">
                      Replace <code className="bg-yellow-500/20 px-1 rounded">./ssh-key-file-name.pem</code> with the path to your SSH private key file.
                      Make sure the key file has the correct permissions: <code className="bg-yellow-500/20 px-1 rounded">chmod 600 ./ssh-key-file-name.pem</code>
                    </p>
                  </div>
                )}
                <div className="text-xs text-gray-400 space-y-1">
                  <p className="font-medium text-gray-300">Details:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li>Subdomain: <code className="bg-gray-800 px-1 rounded">{vm.public_subdomain}</code></li>
                    <li>Username: <code className="bg-gray-800 px-1 rounded">{username}</code></li>
                    <li>OS Image: <code className="bg-gray-800 px-1 rounded">{vm.os_image}</code></li>
                    <li>Authentication: {vm.ssh_key_id ? 'SSH Key (use -i flag with your key file)' : 'Password (will be prompted)'}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-blue-500/30">
              <p className="text-gray-400 text-xs">
                {vm.ssh_key_id
                  ? 'SSH key is configured. Use the -i flag with your private key file path.'
                  : 'Password authentication. Set your SSH password below, or configure an SSH key for key-based authentication.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Set SSH Password Section */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          SSH Password
        </label>
        {showPasswordForm ? (
          <div className="space-y-3">
            <input
              type="password"
              placeholder="Enter password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSetPassword}
                disabled={isUpdating}
                className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Updating...' : 'Set Password'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordForm(false);
                  setPassword('');
                  setConfirmPassword('');
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowPasswordForm(true)}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
          >
            Set SSH Password
          </button>
        )}
      </div>
    </div>
  );
};

